import type { CurrentSeasonArea } from '@protector/entities/property/models';
import type { Region } from '@protector/entities/region/models';
import type { Season } from '@protector/entities/season/models';
import { isActive } from '@protector/entities/season/utils';
import dayjs from 'dayjs';
import { forEach } from 'lodash';
import type { SeasonField } from '@protector/entities/season-field/models';

export { isActive } from '@protector/entities/season/utils';

export const filterSeasonsActive = (seasons: Season[]): Season[] => seasons.filter(season => isActive(season));

export const extractActiveSeasonsIds = (seasons: Season[]): string[] =>
  seasons.reduce((accumulator, season) => {
    if (isActive(season)) {
      accumulator.push(season.id);
    }
    return accumulator;
  }, [] as string[]);

export const getDaeByEmergenceDay = (region?: Region, seasonAreas?: CurrentSeasonArea[], flag?: boolean | string | null): number => {
  if (!region?.current_info?.emergence_day) return 0;

  const DAE = dayjs().diff(region?.current_info?.emergence_day, 'day');
  const validationDAE = DAE >= 0 ? DAE : 0;

  if (!flag) return validationDAE;

  if (region?.current_info?.harvest_day && dayjs(region?.current_info?.harvest_day).isBefore(dayjs())) {
    return dayjs(region?.current_info.harvest_day).diff(region?.current_info.emergence_day, 'day');
  }

  const areaEndAt = (Array.isArray(seasonAreas) ? seasonAreas : []).find(seasonArea => seasonArea.areaId === region?.id)?.endsAt;
  if (areaEndAt && dayjs(areaEndAt).isBefore(dayjs())) {
    return dayjs(areaEndAt).diff(region?.current_info?.emergence_day, 'day');
  }
  return validationDAE;
};

export const getDaeInSeaonArea = (seasonArea?: CurrentSeasonArea, seasonAreas?: CurrentSeasonArea[]): number => {
  const DAE = dayjs().diff(seasonArea?.emergencyDate, 'day');
  const validationDAE = DAE >= 0 ? DAE : 0;

  if (seasonArea?.harvestingDate && dayjs(seasonArea?.harvestingDate).isBefore(dayjs())) {
    return dayjs(seasonArea?.harvestingDate).diff(seasonArea?.emergencyDate, 'day');
  }

  const areaEndAt = (Array.isArray(seasonAreas) ? seasonAreas : []).find(
    currentSeasonArea => currentSeasonArea.areaId === seasonArea?.areaId
  )?.endsAt;
  if (areaEndAt && dayjs(areaEndAt).isBefore(dayjs())) {
    return dayjs(areaEndAt).diff(seasonArea?.emergencyDate, 'day');
  }
  return validationDAE;
};

export const getDAEInSeasonFieldV2 = (seasonField?: SeasonField): number => {
  if (!seasonField) return 0;

  const DAE = dayjs().diff(seasonField?.emergency_date, 'day');
  const validationDAE = DAE >= 0 ? DAE : 0;

  if (seasonField?.emergency_date && seasonField?.harvesting_date && dayjs(seasonField.harvesting_date).isBefore(dayjs())) {
    return dayjs(seasonField.harvesting_date).diff(seasonField.emergency_date, 'day');
  }

  const fieldEndDate = seasonField?.end_date;

  if (fieldEndDate && dayjs(fieldEndDate).isBefore(dayjs())) {
    return dayjs(fieldEndDate).diff(seasonField?.emergency_date, 'day');
  }
  return validationDAE;
};

export const getActiveSeasonsIds = (seasons?: Season[]): string[] => {
  const activeSeasonsIds: string[] = [];

  forEach(seasons, season => {
    if (isActive(season)) {
      activeSeasonsIds.push(season.id);
    }
  });

  return activeSeasonsIds;
};

export const getActiveSeasonsIdsOrMostRecent = (seasons?: Season[]): { seasonsID: string[]; hasActiveSeason: boolean } => {
  if (!seasons?.length) {
    return { seasonsID: [], hasActiveSeason: false };
  }

  const activeSeasonsIds = getActiveSeasonsIds(seasons);

  if (activeSeasonsIds.length > 0) {
    return { seasonsID: activeSeasonsIds, hasActiveSeason: true };
  }

  const [mostRecentSeason] = [...seasons].sort((previousSeason, nextSeason) => {
    const nextSeasonEndDate = new Date(nextSeason.end_date).getTime();
    const previousSeasonEndDate = new Date(previousSeason.end_date).getTime();

    if (nextSeasonEndDate !== previousSeasonEndDate) {
      return nextSeasonEndDate - previousSeasonEndDate;
    }

    const nextSeasonStartDate = new Date(nextSeason.start_date).getTime();
    const previousSeasonStartDate = new Date(previousSeason.start_date).getTime();
    // In case there are two seasons with the same end date, we want to return the most recent one based on the start date
    return nextSeasonStartDate - previousSeasonStartDate;
  });

  return mostRecentSeason?.id ? { seasonsID: [mostRecentSeason.id], hasActiveSeason: false } : { seasonsID: [], hasActiveSeason: false };
};
